import { el } from "../../lib/dom";

/**
 * Shared helpers for widget components:
 *   - fetchWidget: GET /bio-api/widgets/:name?... with normalized response.
 *   - widgetShell: returns the styled container + helpers to swap body.
 *   - formatCount: pretty "12.3k / 1.2M" number formatter.
 *   - elapsed: "2m ago" style relative time.
 */

export type WidgetEnvelope<T> = {
  ok: boolean;
  data?: T;
  cached?: boolean;
  stale?: boolean;
  error?: string;
};

export async function fetchWidget<T>(
  name: string,
  query: Record<string, string>,
  options: { signal?: AbortSignal; retries?: number; retryDelayMs?: number } = {},
): Promise<WidgetEnvelope<T>> {
  const qs = new URLSearchParams(query).toString();
  const url = `/bio-api/widgets/${name}${qs ? `?${qs}` : ""}`;
  const retries = options.retries ?? 3;
  const baseDelay = options.retryDelayMs ?? 800;

  let lastError = "unavailable";
  for (let attempt = 0; attempt <= retries; attempt++) {
    // Bail immediately if the caller aborted between retries (e.g. the
    // widget was torn down on template switch).
    if (options.signal?.aborted) {
      return { ok: false, error: "aborted" };
    }
    try {
      const r = await fetch(url, { signal: options.signal });
      const json = (await r.json().catch(() => ({}))) as WidgetEnvelope<T>;
      if (r.ok) return json;
      // 4xx errors (bad config, missing creds, etc.) are deterministic —
      // don't retry those, surface them straight away so the user sees the
      // real reason (requires STEAM_API_KEY, bad uid, etc.).
      if (r.status >= 400 && r.status < 500) {
        return { ok: false, error: json.error ?? `HTTP ${r.status}` };
      }
      lastError = json.error ?? `HTTP ${r.status}`;
    } catch (err) {
      // Caller-triggered abort: don't retry, don't log.
      if ((err as { name?: string })?.name === "AbortError") {
        return { ok: false, error: "aborted" };
      }
      lastError = (err as Error).message || "network error";
    }
    if (attempt < retries) {
      // Linear backoff with a small jitter cap; transient CF hiccups and
      // cold-start lag usually clear within a couple seconds.
      const delay = baseDelay * (attempt + 1) + Math.random() * 300;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  return { ok: false, error: lastError };
}

/**
 * Standard widget card: title, optional icon, body area. Caller fills the
 * body with loading/error/data states.
 */
export function widgetShell(opts: {
  kind: string;
  title: string;
  accent?: string;
}): {
  root: HTMLElement;
  body: HTMLElement;
  setLoading(label?: string): void;
  setError(message: string): void;
} {
  const root = el("article", {
    class: `widget widget--${opts.kind}`,
    style: opts.accent ? `--widget-accent:${opts.accent}` : null,
  });
  const header = el("header", { class: "widget__header" }, [
    el("span", { class: "widget__title" }, [opts.title]),
  ]);
  const body = el("div", { class: "widget__body" });
  root.append(header, body);

  return {
    root,
    body,
    setLoading(label = "loading…") {
      body.innerHTML = "";
      body.append(el("div", { class: "widget__loading" }, [label]));
    },
    setError(message: string) {
      body.innerHTML = "";
      body.append(el("div", { class: "widget__error" }, [message]));
    },
  };
}

export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  if (n < 1_000_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return `${(n / 1_000_000_000).toFixed(1)}B`;
}

export function elapsed(iso: string | number): string {
  const t = typeof iso === "number" ? iso : Date.parse(iso);
  const diff = Date.now() - t;
  if (!Number.isFinite(diff) || diff < 0) return "just now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
